/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  DefaultUrlSerializer,
  NavigationEnd,
  ResolveFn,
  Router,
  RouterState,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { CodeDeploymentRouteParams } from '@app/code-deployment/types';
import { RouterNavigatedAction, RouterNavigationAction } from '@ngrx/router-store';
import { last, nth } from 'lodash-es';
import { Observable, firstValueFrom } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export const getAllChildren = <T extends { children: T[] }>(route: T): T[] =>
  route.children.length ? [...route.children, ...route.children.map(getAllChildren).flat()] : [];

export const getAllRouteParams = <Params extends Record<string, string>>(
  route: ActivatedRouteSnapshot,
): Partial<Params> =>
  [route, ...getAllChildren(route)].reduce((acc, route) => ({ ...acc, ...route.params }), {} as Partial<Params>);

export const getRoutePath = (route: ActivatedRouteSnapshot): string[] =>
  route.pathFromRoot
    .map(({ url }) => url)
    .flat()
    .map(({ path }) => path);

export const getRelativeToRoute = (
  routerState: RouterState,
  relativeToLevelsBack?: number,
): ActivatedRoute | undefined => {
  const items = getAllChildren(routerState.root);
  return relativeToLevelsBack ? nth(items, -relativeToLevelsBack) : last(items);
};

export const stripOutlets = (url: string): string => url.replace(/\(.+\)/, '');

export type ResolveDataTyped<T extends Record<any, any>> = {
  [P in keyof T]: ResolveFn<T[P]>;
};

export type ActivatedRouteSnapshotTyped<T extends Record<any, any>> = ActivatedRouteSnapshot & {
  data: T;
};

type RouterAction = RouterNavigatedAction | RouterNavigationAction;
const isCurrentScreen = (path: string, action: RouterAction) => {
  const routeChildren = getAllChildren(action.payload.routerState.root);
  return routeChildren.some((child) => child.routeConfig?.path === path);
};
export const getClusterIdByPathMatch = (path: string) => (action$: Observable<RouterAction>) =>
  action$.pipe(
    map((action) => {
      const root = action.payload.routerState.root;
      const routeParams = getAllRouteParams<CodeDeploymentRouteParams>(root);
      return isCurrentScreen(path, action) ? routeParams.clusterId : undefined;
    }),
  );

const makeTreeIterator = <T>(getChildren: (item: T | null) => T[] | null) =>
  function* iterate(item: T | null): Generator<T, void, void> {
    if (item) {
      yield item;
      const children = getChildren(item);
      if (children) {
        for (const child of children) {
          yield* iterate(child);
        }
      }
    }
  };

export const routeIterator = makeTreeIterator((item: ActivatedRouteSnapshot | null) => item?.children ?? null);

export const waitForNavigation = async (router: Router) => {
  const navigation = router.getCurrentNavigation();
  if (navigation) {
    await firstValueFrom(router.events.pipe(filter((event) => event instanceof NavigationEnd)));
  }
};

export const mapUrlTreePrimarySegments = (urlTree: UrlTree, fn: (segment: UrlSegment) => UrlSegment): UrlTree => {
  const urlSerializer = new DefaultUrlSerializer();
  const newUrlTree = urlSerializer.parse(urlSerializer.serialize(urlTree)); // Clones the UrlTree

  if (newUrlTree.root.children.primary?.segments) {
    newUrlTree.root.children.primary.segments = newUrlTree.root.children.primary.segments.map((segment) => fn(segment));
  }

  return newUrlTree;
};

export const urlTreesEqual = (a: UrlTree, b: UrlTree) => a.toString() === b.toString();
